import { CheckIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Icon, Text, Tooltip } from "@chakra-ui/react";
import { FencyProvider } from "@fencyai/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaMagic } from "react-icons/fa";
import type { ContractNature } from "../../autogen/bff-api";
import { fencyPromise } from "../fency/fencySetup";
import { useFency } from "../fency/useFency";
import { useCpvCodes } from "./useCpvCodes";
import { useCpvSuggestions } from "./useCpvSuggestions";

export type CpvSuggestionContext = {
  title: string;
  description: string;
  contractType?: ContractNature;
};

type CpvSuggestionsProps = {
  context: CpvSuggestionContext;
  selected: string[];
  onAdd: (code: string) => void;
  isDisabled?: boolean;
};

const SUGGESTION_MODEL = "anthropic/claude-sonnet-4.6" as const;

export const CpvSuggestions = (props: CpvSuggestionsProps) => {
  const { fetchCreateStreamClientToken, fetchCreateStructuredChatCompletionClientToken } = useFency();
  return (
    <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
      <CpvSuggestionsInner {...props} fetchClientToken={fetchCreateStructuredChatCompletionClientToken} />
    </FencyProvider>
  );
};

type InnerProps = CpvSuggestionsProps & {
  fetchClientToken: () => Promise<{ clientToken: string }>;
};

const CpvSuggestionsInner = ({ context, selected, onAdd, isDisabled, fetchClientToken }: InnerProps) => {
  const { t, i18n } = useTranslation();
  const { codeIndex } = useCpvCodes();
  const isNorwegian = i18n.language.startsWith("no");
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const { suggest, suggestions, isLoading, hasError, clear } = useCpvSuggestions({
    model: SUGGESTION_MODEL,
    fetchClientToken,
    codeIndex,
  });

  const description = useMemo(() => htmlToPlainText(context.description), [context.description]);
  const hasContext = context.title.trim() !== "" || description !== "";

  const handleSuggest = () =>
    suggest({
      title: context.title.trim(),
      description,
      contractType: context.contractType,
      language: isNorwegian ? "no" : "en",
    });

  return (
    <Box mt="3">
      <Tooltip label={t("Add a title or description first")} isDisabled={hasContext} shouldWrapChildren>
        <Button
          size="sm"
          variant="outline"
          colorScheme="teal"
          leftIcon={<Icon as={FaMagic} />}
          onClick={handleSuggest}
          isLoading={isLoading}
          loadingText={t("Suggesting…")}
          isDisabled={isDisabled || !hasContext}
        >
          {t("Suggest CPV codes with AI")}
        </Button>
      </Tooltip>

      {hasError && (
        <Text mt="2" fontSize="sm" color="smError">
          {t("Couldn't generate suggestions. Please try again.")}
        </Text>
      )}

      {suggestions && (
        <Box mt="2" borderWidth="1px" borderColor="smBorder" borderRadius="md" p="3" bg="smBackground">
          <Flex justify="space-between" align="center" mb="2">
            <Text fontSize="sm" fontWeight="semibold">
              {t("AI suggestions")}
            </Text>
            <Button variant="link" size="xs" colorScheme="teal" onClick={clear}>
              {t("Dismiss")}
            </Button>
          </Flex>

          {suggestions.length === 0 ? (
            <Text fontSize="sm" color="smMuted">
              {t("No suggestions found")}
            </Text>
          ) : (
            <Flex direction="column" gap="2">
              {suggestions.map((s) => {
                const isAdded = selectedSet.has(s.code);
                const name = isNorwegian ? s.nameNb : s.nameEn;
                return (
                  <Flex key={s.code} align="flex-start" justify="space-between" gap="3">
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {s.code} — {name}
                      </Text>
                      {s.reason && (
                        <Text fontSize="xs" color="smMuted">
                          {s.reason}
                        </Text>
                      )}
                    </Box>
                    <Button
                      size="xs"
                      flexShrink={0}
                      colorScheme="teal"
                      variant={isAdded ? "ghost" : "solid"}
                      leftIcon={isAdded ? <CheckIcon /> : undefined}
                      isDisabled={isAdded}
                      onClick={() => onAdd(s.code)}
                    >
                      {isAdded ? t("Added") : t("Add")}
                    </Button>
                  </Flex>
                );
              })}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
};

const htmlToPlainText = (html: string): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
};
